import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminChangePassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [show, setShow] = useState({ new_password: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/management/change-password", { new_password: form.new_password });
      toast.success("Password changed successfully. Please continue.");
      await refresh();
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-[hsl(var(--zt-teal))] px-8 py-8">
          <div className="w-12 h-12 rounded-full bg-[hsl(var(--zt-gold))]/20 grid place-items-center mb-4">
            <KeyRound size={22} className="text-[hsl(var(--zt-gold))]" />
          </div>
          <h1 className="font-heading uppercase tracking-[0.15em] text-white text-lg font-bold">
            Set New Password
          </h1>
          <p className="text-white/60 text-sm mt-1 font-body">
            For security, you must change your temporary password before continuing.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-8 space-y-5">
          <div>
            <p className="text-xs font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 mb-1">
              Signed in as
            </p>
            <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{user?.email}</p>
          </div>

          {[
            { key: "new_password", label: "New Password" },
            { key: "confirm", label: "Confirm Password" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1.5">
                {label}
              </label>
              <div className="relative">
                <input
                  type={show[key] ? "text" : "password"}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  required
                  minLength={8}
                  className="w-full h-10 px-4 pr-10 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/40 font-body"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/40 hover:text-[hsl(var(--zt-teal))]"
                >
                  {show[key] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          ))}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-[hsl(var(--zt-teal))] text-white font-heading uppercase tracking-wider text-xs font-bold hover:bg-[hsl(var(--zt-teal))]/90 transition-colors disabled:opacity-60"
          >
            {loading ? "Saving…" : "Set New Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
